import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Settings, Link2, Trash2, CheckCircle2, Zap, ArrowRight,
  AlertCircle, Loader2, Globe, Eye, EyeOff, RefreshCw, Plug, Database, Cloud, BookOpen, Building2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import { getGlobalPropertyId, setGlobalProperty } from './propertySync';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const portalApi = axios.create({ baseURL: API_URL });

function parseApiError(err) {
  const data = err.response?.data;
  if (!data) return err.message || 'Network error';
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || String(d)).join(', ');
  return err.message || 'Request failed';
}

const PMS_OPTIONS = [
  { 
    id: 'beds24', 
    name: 'Beds24', 
    desc: 'Channel manager & booking engine', 
    available: true, 
    logo: '/logos/beds24.png',
    color: 'from-orange-500 to-amber-500',
    features: ['Auto-sync bookings', 'Real-time updates', 'Invoice status sync']
  },
  { 
    id: 'cloudbeds', 
    name: 'Cloudbeds', 
    desc: 'Lightweight booking & record management', 
    available: false, 
    logo: '/logos/cloudbeds.png',
    color: 'from-blue-500 to-cyan-500',
    features: ['Full PMS sync', 'Guest data import', 'Rate management']
  },
  { 
    id: 'siteminder', 
    name: 'SiteMinder', 
    desc: 'Global distribution platform', 
    available: false, 
    logo: '/logos/siteminder.png',
    color: 'from-purple-500 to-pink-500',
    features: ['Channel sync', 'Booking engine', 'Analytics']
  },
  { 
    id: 'hotelrunner', 
    name: 'HotelRunner', 
    desc: 'Distribution & direct booking', 
    available: false, 
    logo: '/logos/hotelrunner.png',
    color: 'from-green-500 to-emerald-500',
    features: ['OTA connections', 'Direct booking', 'Rate parity']
  },
  { 
    id: 'manual', 
    name: 'Manual Entry', 
    desc: 'Enter bookings directly', 
    available: true, 
    logo: '/logos/manual-entry.png',
    color: 'from-slate-500 to-gray-500',
    features: ['Full control', 'No API needed', 'Excel upload']
  },
];

function TokenGuide({ pmsType }) {
  if (pmsType !== 'beds24') return null;
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-100 dark:border-blue-900 rounded-xl p-5" data-testid="beds24-guide">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <p className="font-semibold text-slate-900 dark:text-white">How to get your API tokens</p>
          <p className="text-sm text-slate-500 dark:text-gray-400">Follow these steps in your Beds24 dashboard</p>
        </div>
      </div>
      <ol className="space-y-3 text-sm text-slate-600 dark:text-gray-300">
        {[
          { step: 'Log in to your Beds24 account', link: 'https://beds24.com' },
          { step: 'Go to Settings (gear icon) → Account → Account Access' },
          { step: 'Scroll to the API section' },
          { step: 'Copy the API Key (Read) — this is your Read Token' },
          { step: 'Copy the API Key (Write) — optional, for status sync' },
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
            <span>
              {item.link ? (
                <>Log in to your <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline font-medium">Beds24 account</a></>
              ) : item.step}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function PortalPmsSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('einvoice_token');

  const [connections, setConnections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedPms, setSelectedPms] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [formData, setFormData] = useState({ read_token: '', write_token: '' });
  const [beds24Properties, setBeds24Properties] = useState([]);
  const [selectedBeds24PropId, setSelectedBeds24PropId] = useState('');
  const [fetchingBeds24Props, setFetchingBeds24Props] = useState(false);

  const [showTokens, setShowTokens] = useState({ read: false, write: false });
  const [globalPropertyId, setGlobalPropertyId] = useState(() => getGlobalPropertyId() || null);

  useEffect(() => {
    const handler = (e) => setGlobalPropertyId(e.detail?.id || null);
    window.addEventListener('property-changed', handler);
    return () => window.removeEventListener('property-changed', handler);
  }, []);

  useEffect(() => {
    if (selectedPms === 'beds24') {
      const existing = connections.find(c => c.pms_type === 'beds24');
      if (existing) {
        setFormData({ read_token: existing.read_token || '', write_token: existing.write_token || '' });
        setSelectedPropertyId(existing.property_id || '');
        setSelectedBeds24PropId(existing.beds24_property_id || '');
      }
    }
  }, [selectedPms, connections]);

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    loadConnections();
    loadProperties();
  }, []);

  const headers = { 'Authorization': `Bearer ${token}` };

  const loadConnections = async () => {
    try {
      const { data } = await portalApi.get('/api/einvoice/portal/pms/connections', { headers });
      setConnections(data.connections || []);
    } catch (err) { console.error('Failed to load PMS connections:', err); }
    finally { setLoading(false); }
  };

  const loadProperties = async () => {
    try {
      const { data } = await portalApi.get('/api/einvoice/portal/properties', { headers });
      setProperties(data.properties || []);
    } catch (err) { console.error('Failed to load properties:', err); }
  };

  const handleConnect = async () => {
    if (!selectedPms) return;
    if (selectedPms === 'beds24' && !selectedPropertyId) { setError('Please select a Staymii property'); return; }
    if (selectedPms === 'beds24' && !selectedBeds24PropId) { setError('Please fetch and select a Beds24 property'); return; }
    setSaving(true); setError(''); setSuccess('');

    try {
      const payload = {
        pms_type: selectedPms,
        property_id: selectedPropertyId || undefined,
        ...(selectedPms === 'beds24' && {
          read_token: formData.read_token,
          write_token: formData.write_token || undefined,
          beds24_prop_id: parseInt(selectedBeds24PropId) || undefined
        })
      };
      await portalApi.post('/api/einvoice/portal/pms/connect', payload, { headers });
      setSuccess(`${PMS_OPTIONS.find(p => p.id === selectedPms)?.name || selectedPms} connected successfully!`);
      toast.success(`${PMS_OPTIONS.find(p => p.id === selectedPms)?.name || selectedPms} connected successfully!`);
      setSelectedPms(null); setSelectedPropertyId(''); setSelectedBeds24PropId(''); setBeds24Properties([]);
      setFormData({ read_token: '', write_token: '' });
      await loadConnections();
    } catch (err) { const msg = parseApiError(err); setError(msg); toast.error(`Connection failed: ${msg}`); }
    finally { setSaving(false); }
  };

  const handleFetchBeds24Properties = async () => {
    if (!formData.read_token) { setError('Please enter the Read Token first'); toast.error('Please enter the Read Token first'); return; }
    setFetchingBeds24Props(true); setError(''); setBeds24Properties([]); setSelectedBeds24PropId('');
    try {
      const { data } = await portalApi.post('/api/einvoice/portal/pms/beds24/fetch-properties', { read_token: formData.read_token }, { headers });
      if (data.properties?.length > 0) { setBeds24Properties(data.properties); setSuccess(`Found ${data.properties.length} property(ies)`); toast.success(`Found ${data.properties.length} Beds24 property(ies)`); }
      else { setError('No properties found in your Beds24 account'); toast.error('No properties found in your Beds24 account'); }
    } catch (err) { const msg = parseApiError(err); setError(msg); toast.error(`Fetch failed: ${msg}`); }
    finally { setFetchingBeds24Props(false); }
  };

  const handleDisconnect = async (pmsType) => {
    if (!window.confirm(`Disconnect ${pmsType}? This will stop syncing bookings.`)) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await portalApi.delete(`/api/einvoice/portal/pms/${pmsType}`, { headers });
      setSuccess(`${pmsType} disconnected.`);
      await loadConnections();
    } catch (err) { setError(parseApiError(err)); }
    finally { setSaving(false); }
  };

  const connectedTypes = connections.map(c => c.pms_type);

  // Filter connections by global property selector
  const filteredConnections = globalPropertyId
    ? connections.filter(c => c.property_id === globalPropertyId || !c.property_id)
    : connections;
  const globalPropertyName = globalPropertyId
    ? properties.find(p => p.id === globalPropertyId)?.property_name
    : null;

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="portal-pms-settings">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25 mb-4">
            <Plug className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">PMS Integrations</h1>
          <p className="text-slate-500 dark:text-gray-400 max-w-md mx-auto">
            Connect your Property Management System or use StayMii Lite to automatically sync bookings and generate e-invoices
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <Select value={globalPropertyId || 'all'} onValueChange={v => { const val = v === 'all' ? null : v; setGlobalPropertyId(val); setGlobalProperty(val, properties); }}>
              <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="integration-property-filter"><SelectValue placeholder="All Properties" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs font-medium text-[#4169E1]" data-testid="viewing-property-label">
              Viewing: {globalPropertyName || 'All Properties'}
            </p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-2" data-testid="pms-error">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 animate-in fade-in slide-in-from-top-2" data-testid="pms-success">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <AlertDescription className="text-emerald-700 dark:text-emerald-300">{success}</AlertDescription>
          </Alert>
        )}

        {/* Connected Systems */}
        {filteredConnections.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Active Connections
                </h2>
              </div>
              <Button variant="ghost" size="sm" onClick={loadConnections} className="text-slate-500 hover:text-slate-700">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid gap-4">
              {filteredConnections.map((conn) => {
                const pmsInfo = PMS_OPTIONS.find(p => p.id === conn.pms_type);
                return (
                  <div
                    key={conn.pms_type}
                    className="group relative overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 p-5 transition-all hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-zinc-900/50"
                    data-testid={`connection-${conn.pms_type}`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${pmsInfo?.color || 'from-slate-500 to-gray-500'} opacity-[0.03]`} />
                    <div className="relative flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${pmsInfo?.color || 'from-slate-500 to-gray-500'} p-[2px] shrink-0`}>
                          <div className="w-full h-full rounded-[10px] bg-white dark:bg-zinc-900 flex items-center justify-center">
                            {pmsInfo?.logo ? (
                              <img src={pmsInfo.logo} alt={pmsInfo?.name} className="w-9 h-9 object-contain" />
                            ) : (
                              <Database className="w-7 h-7 text-slate-600" />
                            )}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 dark:text-white text-lg leading-tight">{pmsInfo?.name || conn.pms_type}</p>
                          <p className="text-sm text-slate-500 dark:text-gray-400 truncate">{pmsInfo?.desc || ''}</p>
                          {conn.property_id && (() => {
                            const prop = properties.find(p => p.id === conn.property_id);
                            return prop ? (
                              <span className="inline-flex items-center gap-1 text-[11px] text-[#4169E1] mt-0.5"><Building2 className="w-3 h-3" />{prop.property_name}</span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 pr-1">
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20" data-testid={`status-${conn.pms_type}`}>
                          <span className="relative flex h-2.5 w-2.5 shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                          </span>
                          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap">Connected</span>
                        </div>
                        {conn.pms_type !== 'manual' && conn.pms_type !== 'skip' && conn.pms_type !== 'none' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all h-8 w-8 p-0"
                            onClick={() => handleDisconnect(conn.pms_type)}
                            data-testid={`disconnect-${conn.pms_type}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Integrations */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-500" />
            {connections.length > 0 ? 'Add Another Integration' : 'Available Integrations'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PMS_OPTIONS.map((pms) => {
              const isConnected = connectedTypes.includes(pms.id);
              const isSelected = selectedPms === pms.id;
              return (
                <button
                  key={pms.id}
                  onClick={() => pms.available && setSelectedPms(isSelected ? null : pms.id)}
                  disabled={!pms.available}
                  className={`relative text-left p-5 rounded-2xl border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-lg shadow-blue-500/10'
                      : isConnected
                        ? 'border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20'
                        : pms.available
                          ? 'border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-slate-300 dark:hover:border-zinc-700 hover:shadow-md'
                          : 'border-slate-100 dark:border-zinc-900 bg-slate-50 dark:bg-zinc-950 opacity-60 cursor-not-allowed'
                  }`}
                  data-testid={`select-pms-${pms.id}`}
                >
                  {/* Status Badges */}
                  {isConnected && (
                    <Badge className="absolute top-3 right-3 bg-emerald-500 text-white border-0 text-[10px] px-2">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                    </Badge>
                  )}
                  {!pms.available && (
                    <Badge variant="outline" className="absolute top-3 right-3 text-[10px] px-2 bg-slate-100 dark:bg-zinc-800">Coming Soon</Badge>
                  )}

                  {/* Logo */}
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${pms.color} p-[2px] mb-4`}>
                    <div className="w-full h-full rounded-[14px] bg-white dark:bg-zinc-900 flex items-center justify-center">
                      <img src={pms.logo} alt={pms.name} className="w-12 h-12 object-contain" />
                    </div>
                  </div>

                  {/* Info */}
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{pms.name}</h3>
                  <p className="text-sm text-slate-500 dark:text-gray-400 mb-3">{pms.desc}</p>

                  {/* Features */}
                  <div className="space-y-1.5">
                    {pms.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-400 dark:text-gray-500">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <div className="absolute bottom-4 right-4">
                      <ArrowRight className="w-5 h-5 text-blue-500 animate-pulse" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Configuration Panel */}
        {selectedPms && (
          <Card className="border-2 border-blue-200 dark:border-blue-900 bg-gradient-to-br from-white to-blue-50/50 dark:from-zinc-900 dark:to-blue-950/20 shadow-xl shadow-blue-500/5 animate-in fade-in slide-in-from-bottom-4">
            <CardHeader className="border-b border-blue-100 dark:border-blue-900/50">
              <CardTitle className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${PMS_OPTIONS.find(p => p.id === selectedPms)?.color} p-[2px]`}>
                  <div className="w-full h-full rounded-[8px] bg-white dark:bg-zinc-900 flex items-center justify-center">
                    <img src={PMS_OPTIONS.find(p => p.id === selectedPms)?.logo} alt="" className="w-6 h-6 object-contain" />
                  </div>
                </div>
                <div>
                  <span className="text-slate-900 dark:text-white">Configure {PMS_OPTIONS.find(p => p.id === selectedPms)?.name}</span>
                  <p className="text-sm font-normal text-slate-500 dark:text-gray-400">Enter your API credentials to connect</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Beds24 Form */}
              {selectedPms === 'beds24' && (
                <>
                  <TokenGuide pmsType="beds24" />

                  {/* Step 1 */}
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-gray-300 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-bold">1</span>
                      Enter Read Token & Fetch Properties
                    </Label>
                    <div className="flex gap-3">
                      <div className="relative flex-1">
                        <Input
                          type={showTokens.read ? 'text' : 'password'}
                          value={formData.read_token}
                          onChange={(e) => { setFormData({ ...formData, read_token: e.target.value }); setBeds24Properties([]); setSelectedBeds24PropId(''); }}
                          placeholder="Paste your Beds24 API Key (Read)"
                          className="pr-10 h-12 text-base"
                          data-testid="pms-read-token"
                        />
                        <button
                          type="button"
                          onClick={() => setShowTokens({ ...showTokens, read: !showTokens.read })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showTokens.read ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button
                        onClick={handleFetchBeds24Properties}
                        disabled={!formData.read_token || fetchingBeds24Props}
                        className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="fetch-beds24-props-btn"
                      >
                        {fetchingBeds24Props ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Fetch
                      </Button>
                    </div>
                  </div>

                  {/* Inline fetch feedback (visible right at the button) */}
                  {error && (
                    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300 animate-in fade-in slide-in-from-top-1" data-testid="pms-fetch-inline-error">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Step 2 */}
                  {beds24Properties.length > 0 && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-gray-300 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-bold">2</span>
                        Select Beds24 Property
                      </Label>
                      <select
                        value={selectedBeds24PropId}
                        onChange={(e) => setSelectedBeds24PropId(e.target.value)}
                        className="w-full h-12 border border-slate-200 dark:border-zinc-700 rounded-xl px-4 text-base bg-white dark:bg-zinc-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="beds24-property-select"
                      >
                        <option value="">-- Select Beds24 property --</option>
                        {beds24Properties.map((p) => (
                          <option key={p.propId} value={p.propId}>{p.name} (ID: {p.propId})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Step 3 */}
                  {beds24Properties.length > 0 && selectedBeds24PropId && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-gray-300 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-bold">3</span>
                        Link to Staymii Property
                      </Label>
                      <select
                        value={selectedPropertyId}
                        onChange={(e) => setSelectedPropertyId(e.target.value)}
                        className="w-full h-12 border border-slate-200 dark:border-zinc-700 rounded-xl px-4 text-base bg-white dark:bg-zinc-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="pms-property-select"
                      >
                        <option value="">-- Select Staymii property --</option>
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>{p.property_name} {p.property_code ? `(${p.property_code})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Write Token */}
                  {beds24Properties.length > 0 && selectedBeds24PropId && selectedPropertyId && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 p-4 bg-slate-50 dark:bg-zinc-800/50 rounded-xl">
                      <Label className="text-sm font-medium text-slate-600 dark:text-gray-400">Write Token (Optional)</Label>
                      <div className="relative">
                        <Input
                          type={showTokens.write ? 'text' : 'password'}
                          value={formData.write_token}
                          onChange={(e) => setFormData({ ...formData, write_token: e.target.value })}
                          placeholder="Paste your Beds24 API Key (Write)"
                          className="pr-10 h-11"
                          data-testid="pms-write-token"
                        />
                        <button
                          type="button"
                          onClick={() => setShowTokens({ ...showTokens, write: !showTokens.write })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showTokens.write ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-gray-400">Enables Staymii to update invoice status in Beds24</p>
                    </div>
                  )}
                </>
              )}

              {/* Manual Entry */}
              {selectedPms === 'manual' && (
                <div className="p-6 bg-slate-50 dark:bg-zinc-800/50 rounded-xl text-center">
                  <Database className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-600 dark:text-gray-300 mb-2">No API configuration needed</p>
                  <p className="text-sm text-slate-500 dark:text-gray-400">
                    With manual entry, you'll enter bookings directly or upload via Excel. You can always connect a PMS later.
                  </p>
                </div>
              )}

              {/* Connect Button */}
              <Button
                onClick={handleConnect}
                disabled={saving || (selectedPms === 'beds24' && (!formData.read_token || !selectedPropertyId || !selectedBeds24PropId))}
                className="w-full h-12 text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25"
                data-testid="connect-pms-btn"
              >
                {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Link2 className="w-5 h-5 mr-2" />}
                {connectedTypes.includes(selectedPms) ? 'Update Connection' : 'Connect Integration'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}
